import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, CalendarDays, Rows3, CheckCircle2, XCircle,
  Clock, Moon, AlertTriangle, MapPin, LogIn, LogOut, X, Gift, Settings2,
  Ban, Loader2, Wifi, WifiOff, TimerReset, Sparkles
} from 'lucide-react';
import SummaryApi from '../../common/SummaryApi';
import api from '../../common/apiClient';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

/* 
  STATUS META — LIGHT BACKGROUND PRECISE COLOR SCHEME
*/
const STATUS_META = {
  present:        { label: 'Present',         textColor: 'text-blue-600',   borderClass: 'border-blue-200',   bgSoft: 'bg-blue-50',     Icon: CheckCircle2 },
  absent:         { label: 'Absent',          textColor: 'text-red-600',    borderClass: 'border-red-200',    bgSoft: 'bg-red-50',      Icon: XCircle },
  on_leave:       { label: 'On Leave',        textColor: 'text-indigo-600', borderClass: 'border-indigo-200', bgSoft: 'bg-indigo-50',   Icon: CalendarDays },
  holiday:        { label: 'Holiday',         textColor: 'text-[#E3A93F]',  borderClass: 'border-[#E3A93F]/20',bgSoft: 'bg-[#E3A93F]/10', Icon: Gift },
  week_off:       { label: 'Week Off',        textColor: 'text-slate-500',  borderClass: 'border-slate-200',  bgSoft: 'bg-slate-50',    Icon: Moon },
  not_configured: { label: 'Not Configured',  textColor: 'text-slate-400',  borderClass: 'border-gray-200',   bgSoft: 'bg-gray-50',     Icon: Ban },
  not_joined:     { label: 'Not Joined',      textColor: 'text-slate-400',  borderClass: 'border-gray-200',   bgSoft: 'bg-gray-50',     Icon: Ban },
  yet_to_checkin: { label: 'Yet to Check-in', textColor: 'text-amber-600',  borderClass: 'border-amber-200',  bgSoft: 'bg-amber-50',    Icon: Clock },
  upcoming:       { label: 'Upcoming',        textColor: 'text-slate-300',  borderClass: 'border-transparent',bgSoft: 'bg-transparent', Icon: CalendarDays },
};

const metaFor = (s) => STATUS_META[s] || STATUS_META.not_configured;

function fmtMinutes(min) {
  if (!min || min <= 0) return '0m';
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', timeZone:'Asia/Kolkata' });
  } catch { return '—'; }
}

function fmtDateLong(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
}

function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }
function firstWeekday(y, m) { return new Date(y, m - 1, 1).getDay(); }
function shortAddress(addr) {
  if (!addr) return '—';
  const parts = addr.split(',').map(s => s.trim());
  return parts.slice(0, 3).join(', ');
}

export default function AttendanceCalendar() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [view, setView] = useState('calendar');
  const [data, setData] = useState([]);
  const [user, setUser] = useState({ firstName: 'User', lastName: '', userId: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [connected, setConnected] = useState(false);

  const loadData = useCallback(async (y, m) => {
    setLoading(true);
    setError(null);
    try {
      const urlPath = SummaryApi?.getMyAttendaceCalendar?.url || "/api/hr/attendance/my-calendar";
      const url = `${urlPath}?year=${y}&month=${m}`;
      const method = SummaryApi?.getMyAttendaceCalendar?.method || "get";
      
      const res = await api({ url, method });
      
      if (res?.data?.success) {
        setData(res.data.data?.calendar || []);
        setUser(prevUser => res.data.data?.user || prevUser);
        setConnected(true);
      } else {
        setError(res?.data?.message || 'API responded without data.');
        setData([]);
        setConnected(false);
      }
    } catch (err) {
      console.error("Error fetching attendance details:", err);
      setError("Could not reach the API.");
      setConnected(false);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(year, month);
  }, [year, month, loadData]);

  function shiftMonth(delta) {
    let m = month + delta, y = year;
    if (m > 12) { m = 1; y += 1; }
    if (m < 1) { m = 12; y -= 1; }
    setMonth(m); setYear(y);
  }

  const dataMap = useMemo(() => {
    const map = {};
    data.forEach(d => { map[d.date] = d; });
    return map;
  }, [data]);

  const stats = useMemo(() => {
    let present = 0, absent = 0, late = 0, totalMinutes = 0, onLeave = 0;
    data.forEach(d => {
      if (d.status === 'present') present++;
      if (d.status === 'absent') absent++;
      if (d.status === 'on_leave') onLeave++;
      if (d.isLate) late++;
      totalMinutes += d.totalWorkingMinutes || 0;
    });
    return { present, absent, late, totalMinutes, onLeave };
  }, [data]);

  const gridCells = useMemo(() => {
    const lead = firstWeekday(year, month);
    const total = daysInMonth(year, month);
    const cells = Array.from({ length: lead }).map(() => null);
    for (let d = 1; d <= total; d++) {
      const ds = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      cells.push(dataMap[ds] || { date: ds, status: 'not_configured' });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month, dataMap]);

  const listRows = useMemo(() => {
    return data.filter(d => d.status !== 'upcoming').slice().sort((a,b)=>a.date.localeCompare(b.date));
  }, [data]);

  return (
    <div className="bg-gray-50 text-slate-800 rounded-xl min-h-screen">
      <div className="flex flex-col gap-4">

        {/* HEADER */}
        <div className="rounded-xl p-4 border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center font-bold text-lg shrink-0 bg-primary text-white shadow-sm">
                {(user.firstName?.[0]||'')+(user.lastName?.[0]||'')}
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold leading-tight tracking-wide text-slate-800">
                  {user.firstName} {user.lastName}
                </h1>
                <div className="text-xs sm:text-sm flex items-center gap-2 text-slate-500 mt-0.5">
                  <span>ID {user.userId}</span>
                  {user.designation && (
                    <>
                      <span className="text-slate-300">•</span>
                      <span>{user.designation}</span>
                    </>
                  )}
                  <span className="text-slate-300">•</span>
                  <span className="flex items-center gap-1">
                    {connected ? <Wifi size={12} className="text-blue-600" /> : <WifiOff size={12} className="text-slate-400" />}
                    {connected ? 'Live API' : 'No Connection'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="bg-white border border-gray-200 flex items-center rounded-lg overflow-hidden shadow-sm">
                <button onClick={()=>shiftMonth(-1)} className="p-2 transition text-slate-600 hover:bg-primary hover:text-white active:scale-95">
                  <ChevronLeft size={18} />
                </button>
                <div className="px-3 text-sm font-semibold whitespace-nowrap select-none text-slate-800 tracking-wide border-x border-gray-100 py-1">
                  {MONTHS[month-1]} {year}
                </div>
                <button onClick={()=>shiftMonth(1)} className="p-2 transition text-slate-600 hover:bg-primary hover:text-white active:scale-95">
                  <ChevronRight size={18} />
                </button>
              </div>

              <div className="bg-gray-100 border border-gray-200/60 flex rounded-lg p-1 gap-1">
                <button
                  onClick={()=>setView('calendar')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition active:scale-95 ${
                    view === 'calendar' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-gray-200'
                  }`}
                >
                  <CalendarDays size={14} /> <span className="hidden xs:inline">Calendar</span>
                </button>
                <button
                  onClick={()=>setView('list')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition active:scale-95 ${
                    view === 'list' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-gray-200'
                  }`}
                >
                  <Rows3 size={14} /> <span className="hidden xs:inline">List</span>
                </button>
              </div>

              <button
                onClick={()=>setShowSettings(s=>!s)}
                className={`p-2.5 rounded-lg transition border border-gray-200 active:scale-95 ${
                  showSettings ? 'text-white bg-primary border-primary' : 'text-slate-600 bg-white hover:bg-gray-50'
                }`}
              >
                <Settings2 size={16} />
              </button>
            </div>
          </div>

          {showSettings && (
            <div className="border-t border-gray-100 mt-4 pt-4 flex flex-col gap-2">
              <div className="text-xs text-slate-500">
                Connection Status: <code>{connected ? 'Live Data Mode' : 'Preview Mode'}</code>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={()=>loadData(year, month)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 text-white transition shadow-sm"
                >
                  Trigger Reload
                </button>
              </div>
              {error && (
                <div className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle size={12}/>{error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
          <StatCard label="Present" value={stats.present} textColor="text-blue-600" bgSoft="bg-blue-50" Icon={CheckCircle2} />
          <StatCard label="Absent" value={stats.absent} textColor="text-red-600" bgSoft="bg-red-50" Icon={XCircle} />
          <StatCard label="On Leave" value={stats.onLeave} textColor="text-indigo-600" bgSoft="bg-indigo-50" Icon={CalendarDays} />
          <StatCard label="Late Arrivals" value={stats.late} textColor="text-amber-600" bgSoft="bg-amber-50" Icon={Clock} />
          <StatCard label="Hours Logged" value={fmtMinutes(stats.totalMinutes)} textColor="text-emerald-600" bgSoft="bg-emerald-50" Icon={TimerReset} className="col-span-2 sm:col-span-1" />
        </div>

        {/* CONTENT PANEL */}
        <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-6 relative min-h-[360px] shadow-sm">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/80 z-10">
              <Loader2 className="animate-spin text-primary" size={28} />
            </div>
          )}

          {view === 'calendar' ? (
            <CalendarGrid cells={gridCells} onSelect={setSelected} />
          ) : (
            <ListRows rows={listRows} onSelect={setSelected} />
          )}

          {!loading && data.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-slate-400">
              <CalendarDays size={32} />
              <p className="text-sm">No records available for this month.</p>
            </div>
          )}
        </div>

       {/* STATUS LEGEND / STATUS GUIDE */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
          <div className="text-xs font-bold text-slate-700 mb-2 border-b border-gray-100 pb-1.5 uppercase tracking-wide flex items-center gap-1.5">
            <Sparkles size={14} className="text-primary" /> Status Guide
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 border-blue-600 flex items-center justify-center bg-blue-50 text-[10px] text-blue-600 font-bold">✓</div>
              <span className="text-slate-600"><strong className="text-blue-600">Present:</strong> Blue Circle</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle size={14} className="text-red-600" />
              <span className="text-slate-600"><strong className="text-red-600">Absent:</strong> Red Icon</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 border-amber-500 flex items-center justify-center bg-amber-50 text-[10px] text-amber-600 font-bold">✓</div>
              <span className="text-slate-600"><strong className="text-amber-600">Late:</strong> Amber Warning Ring</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-md flex items-center justify-center bg-indigo-50 border border-indigo-200 text-[9px] text-indigo-600 font-extrabold">CL</div>
              <span className="text-slate-600"><strong className="text-indigo-600">Leave:</strong> Leave Type (e.g. CL / SL)</span>
            </div>
            <div className="flex items-center gap-2">
              <Moon size={14} className="text-slate-500" />
              <span className="text-slate-600"><strong className="text-slate-500">Week Off:</strong> Gray Moon</span>
            </div>
          </div>
        </div>

        <div className="text-slate-400 text-xs text-center pb-4 flex items-center justify-center gap-1">
          Attendance overview • times shown in IST
        </div>
      </div>

      {selected && <DetailDrawer day={selected} onClose={()=>setSelected(null)} />}
    </div>
  );
}

/* ---------------------------------------------------------------
   STAT CARD SUB-COMPONENT
---------------------------------------------------------------- */
function StatCard({ label, value, textColor, bgSoft, Icon, className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-3 sm:p-4 flex items-center gap-3 shadow-sm ${className}`}>
      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${bgSoft}`}>
        <Icon size={18} className={textColor} />
      </div>
      <div className="min-w-0">
        <div className="text-base sm:text-lg font-bold leading-tight truncate text-slate-800">
          {value}
        </div>
        <div className="text-xs font-semibold text-slate-500 truncate">{label}</div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   CALENDAR GRID SUB-COMPONENT
---------------------------------------------------------------- */
function CalendarGrid({ cells, onSelect }) {
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
        {WEEKDAYS.map(w => (
          <div key={w} className="text-slate-500 text-center text-xs font-semibold py-1">
            <span className="sm:hidden">{w[0]}</span>
            <span className="hidden sm:inline">{w}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {cells.map((day, i) => day ? <DayCell key={day.date} day={day} onSelect={onSelect} /> : <div key={`blank-${i}`} />)}
      </div>
    </div>
  );
}

function DayCell({ day, onSelect }) {
  const meta = metaFor(day.status);
  const Icon = meta.Icon;
  const dateNum = parseInt(day.date.split('-')[2], 10);
  const isPresent = day.status === 'present';
  const isLeave = day.status === 'on_leave';
  const target = 480; // Reference of 8 hours
  const pct = isPresent ? Math.min(100, Math.round((day.totalWorkingMinutes/target)*100)) : 0;
  
  // FIX: Late-க்கு மஞ்சள் வண்ணமும், Present-க்கு நீல வண்ணமும் பயன்படுத்தப்படுகிறது (சிவப்பு Absent-க்கு மட்டுமே!)
  const ringColor = day.isLate ? '#E3A93F' : '#3E63FF';
  const clickable = day.status !== 'upcoming';

  return (
    <button
      onClick={()=> clickable && onSelect(day)}
      disabled={!clickable}
      className={`relative rounded-lg h-16 sm:h-24 p-1.5 sm:p-2 flex flex-col items-start text-left transition hover:border-primary/50 border ${
        clickable ? 'bg-white border-gray-200 hover:shadow-sm cursor-pointer' : 'bg-gray-50/50 border-gray-100 text-slate-300 cursor-default'
      } ${day.status === 'upcoming' ? 'opacity-40' : 'opacity-100'}`}
    >
      <div className="flex items-center justify-between w-full">
        <span className="text-xs sm:text-sm font-semibold text-slate-700">{dateNum}</span>
        
        {/* Present state circular track */}
        {isPresent ? (
          <div 
            className="relative w-4 h-4 sm:w-5 sm:h-5 rounded-full shrink-0" 
            style={{ background:`conic-gradient(${ringColor} ${pct*3.6}deg, rgba(0,0,0,0.08) 0deg)` }}
          >
            <div className="absolute inset-[2px] rounded-full bg-white" />
          </div>
        ) : isLeave ? (
          /* Populated leave code (CL, SL, PL) */
          <div className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-600 uppercase shrink-0">
            {day.leaveDetails?.leaveType?.code || 'LV'}
          </div>
        ) : (
          <Icon size={12} className={`shrink-0 sm:w-[14px] sm:h-[14px] ${meta.textColor}`} />
        )}
      </div>

      <div className="mt-auto w-full">
        {isPresent && (
          <div className="text-[10px] sm:text-xs font-semibold truncate" style={{ color: ringColor }}>
            {fmtMinutes(day.totalWorkingMinutes)}
          </div>
        )}
        {day.status !== 'present' && day.status !== 'upcoming' && (
          <div className={`text-[10px] sm:text-xs font-semibold truncate ${meta.textColor}`}>
            {isLeave ? (day.leaveDetails?.leaveType?.name || meta.label) : meta.label}
          </div>
        )}
        {day.isLate && (
          <div className="text-[9px] sm:text-[10px] truncate text-amber-600 font-bold">+{day.lateMinutes}m late</div>
        )}
      </div>

      {day.holidayName && (
        <div className="absolute -top-1 -right-1 rounded-full p-1 bg-[#E3A93F]/15 text-[#E3A93F] border border-[#E3A93F]/20">
          <Gift size={9} />
        </div>
      )}
      {day.missedCheckout && (
        <div className="absolute bottom-1 right-1 text-red-600">
          <AlertTriangle size={10} />
        </div>
      )}
    </button>
  );
}

/* ---------------------------------------------------------------
   LIST VIEW SUB-COMPONENT
---------------------------------------------------------------- */
function ListRows({ rows, onSelect }) {
  if (!rows.length) return null;
  return (
    <div className="flex flex-col divide-y divide-gray-100">
      {rows.map(day => {
        const meta = metaFor(day.status);
        const Icon = meta.Icon;
        const d = new Date(day.date + 'T00:00:00');
        const isLeave = day.status === 'on_leave';
        return (
          <button
            key={day.date}
            onClick={()=>onSelect(day)}
            className="flex items-center gap-3 sm:gap-4 py-3 text-left w-full hover:bg-gray-50 rounded-lg px-2 transition border-gray-100"
          >
            <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex flex-col items-center justify-center shrink-0 ${meta.bgSoft}`}>
              <span className={`text-sm font-bold leading-none ${meta.textColor}`}>{d.getDate()}</span>
              <span className="text-[9px] uppercase text-slate-400">{WEEKDAYS[d.getDay()]}</span>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm font-semibold flex items-center gap-1 ${meta.textColor}`}>
                  <Icon size={13} /> {isLeave ? `${day.leaveDetails?.leaveType?.name || meta.label} (${day.leaveDetails?.leaveType?.code || 'Leave'})` : meta.label}
                </span>
                {day.holidayName && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 bg-[#E3A93F]/15 text-[#E3A93F] border border-[#E3A93F]/20">
                    <Gift size={9}/>{day.holidayName}
                  </span>
                )}
                {day.isLate && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold border border-amber-100">
                    Late {day.lateMinutes}m
                  </span>
                )}
                {day.missedCheckout && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 bg-red-50 text-red-600 border border-red-100">
                    <AlertTriangle size={9}/>Missed checkout
                  </span>
                )}
              </div>
              <div className="text-xs mt-0.5 truncate text-slate-500">
                {day.firstIn || day.lastOut ? `${fmtTime(day.firstIn)} → ${fmtTime(day.lastOut)}` : 'No punch records'}
              </div>
            </div>

            <div className="text-right shrink-0">
              <div className="text-sm font-semibold text-slate-800">{fmtMinutes(day.totalWorkingMinutes)}</div>
              <div className="text-[10px] text-slate-400">worked</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------
   DETAIL DRAWER SUB-COMPONENT
---------------------------------------------------------------- */
function DetailDrawer({ day, onClose }) {
  const meta = metaFor(day.status);
  const Icon = meta.Icon;
  const isLeave = day.status === 'on_leave';
  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end bg-black/40 backdrop-blur-sm transition-opacity duration-300" 
      onClick={onClose}
    >
      <div
        onClick={e=>e.stopPropagation()}
        className="w-full sm:max-w-md h-full rounded-t-2xl sm:rounded-t-none sm:rounded-l-2xl p-5 sm:p-6 overflow-y-auto max-h-[85vh] sm:max-h-full bg-white border-l border-gray-200 shadow-2xl transition-transform duration-300 transform"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 ${meta.bgSoft} ${meta.textColor}`}>
                <Icon size={12}/>{isLeave ? `${day.leaveDetails?.leaveType?.name || meta.label} (${day.leaveDetails?.leaveType?.code || 'Leave'})` : meta.label}
              </span>
            </div>
            <h2 className="text-lg font-bold mt-2 text-slate-800">{fmtDateLong(day.date)}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-slate-400 transition active:scale-95">
            <X size={18} />
          </button>
        </div>

        {day.holidayName && (
          <div className="rounded-lg px-3 py-2 text-sm flex items-center gap-2 mb-4 bg-[#E3A93F]/15 text-[#E3A93F] border border-[#E3A93F]/20">
            <Gift size={14} /> {day.holidayName}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mb-4">
          <MiniStat label="First In" value={fmtTime(day.firstIn)} Icon={LogIn} textColor="text-blue-600" />
          <MiniStat label="Last Out" value={fmtTime(day.lastOut)} Icon={LogOut} textColor="text-red-600" />
          <MiniStat label="Worked" value={fmtMinutes(day.totalWorkingMinutes)} Icon={TimerReset} textColor="text-slate-800" />
          <MiniStat label="Late By" value={day.isLate ? `${day.lateMinutes}m` : '—'} Icon={Clock} textColor={day.isLate ? "text-amber-600" : "text-slate-400"} />
        </div>

        {day.missedCheckout && (
          <div className="rounded-lg px-3 py-2 text-xs flex items-center gap-2 mb-4 bg-red-50 text-red-600 border border-red-100">
            <AlertTriangle size={13}/> Checkout was missed on this day.
          </div>
        )}

        {(day.punches && day.punches.length > 0) && (
          <div className="mb-4">
            <div className="text-xs font-semibold mb-2 uppercase tracking-wide text-slate-400">Punch Timeline</div>
            <div className="flex flex-col gap-2">
              {day.punches.map((p, idx) => (
                <div key={idx} className="rounded-lg p-3 flex items-start gap-2 bg-gray-50 border border-gray-200/80 shadow-sm">
                  {p.type === 'in'
                    ? <LogIn size={14} className="mt-0.5 shrink-0 text-blue-600" />
                    : <LogOut size={14} className="mt-0.5 shrink-0 text-red-600" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">{p.type === 'in' ? 'Checked in' : 'Checked out'} • {fmtTime(p.time)}</div>
                    <div className="text-xs flex items-center gap-1 mt-0.5 text-slate-500">
                      <MapPin size={11} className="shrink-0 text-slate-400" /><span className="truncate">{shortAddress(p.location?.address)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Beautiful styled block instead of raw JSON block with quotes */}
        {day.leaveDetails && (
          <div className="mb-4 bg-slate-50 border border-slate-200/80 rounded-xl p-4 shadow-sm">
            <div className="text-xs font-semibold mb-3 uppercase tracking-wide text-slate-400">Leave Details</div>
            <div className="flex flex-col gap-2.5">
              {/* 1. Leave Type */}
              <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                <span className="text-slate-500 font-medium">Leave Type</span>
                <span className="text-slate-800 font-semibold">{day.leaveDetails.leaveType?.name || "N/A"}</span>
              </div>

              {/* 2. Leave Code */}
              <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                <span className="text-slate-500 font-medium">Leave Code</span>
                <span className="text-indigo-600 font-bold bg-indigo-50 px-2 py-0.5 rounded-md text-xs uppercase">
                  {day.leaveDetails.leaveType?.code || "N/A"}
                </span>
              </div>

              {/* 3. Reason */}
              <div className="flex justify-between items-start text-sm pt-0.5">
                <span className="text-slate-500 font-medium">Reason</span>
                <span className="text-slate-700 max-w-[65%] text-right break-words">{day.leaveDetails.reason || "N/A"}</span>
              </div>
            </div>
          </div>
        )}
        {day.permissionDetails && (
          <InfoBlock title="Permission Details" data={day.permissionDetails} textColor="text-blue-600" />
        )}
        {day.compOffEarned && (
          <InfoBlock title="Comp-off Earned" data={day.compOffEarned} textColor="text-blue-600" />
        )}

        {(!day.punches || day.punches.length === 0) && !day.leaveDetails && !day.permissionDetails && (
          <div className="text-sm text-center py-6 text-slate-400">No punch activity recorded for this day.</div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, Icon, textColor }) {
  return (
    <div className="bg-gray-50 border border-gray-200/80 rounded-lg p-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-wide flex items-center gap-1 mb-1 text-slate-400">
        <Icon size={11}/>{label}
      </div>
      <div className={`text-sm font-bold ${textColor}`}>{value}</div>
    </div>
  );
}

function InfoBlock({ title, data, textColor }) {
  return (
    <div className="mb-4">
      <div className="text-xs font-semibold mb-2 uppercase tracking-wide text-slate-400">{title}</div>
      <div className="bg-gray-50 border border-gray-200/80 rounded-lg p-3 text-sm shadow-sm">
        {typeof data === 'object' ? (
          <pre className={`whitespace-pre-wrap break-words text-xs ${textColor}`}>{JSON.stringify(data, null, 2)}</pre>
        ) : (
          <span className={textColor}>{String(data)}</span>
        )}
      </div>
    </div>
  );
}